import {
  COUPON_REGEX,
  LENGTH_COMMON_SHORT_ID,
  MAX_LENGTH_SHORT_ID,
  MAX_LENGTH_SKU_CHAR,
  MAX_LENGTH_SKU_NUMBER,
  MIN_LENGTH_SHORT_ID,
  SHORT_ID_REGEX,
} from './constants';
import {
  checkFirstCharIsNotSpace,
  checkOnlyNumber,
  checkOnlySpaces,
  isEmptyString,
  validateEmail,
  validateURL,
} from './function';

export const validateShortIDFieldForm = (
  type?: 'COMMON' | 'MIN_MAX',
  name?: string
) => [
  {
    required: true,
    message: `Bạn chưa nhập ${name || 'Short ID'}!`,
  },
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        const nameValue = name || 'Short ID';
        if (value) {
          const typeValue = type || 'MIN_MAX';
          if (typeValue == 'MIN_MAX') {
            if (
              value.length < MIN_LENGTH_SHORT_ID ||
              value.length > MAX_LENGTH_SHORT_ID
            ) {
              if (value.length < MIN_LENGTH_SHORT_ID) {
                return Promise.reject(
                  `${nameValue} phải đạt ${MIN_LENGTH_SHORT_ID} ký tự!`
                );
              }

              if (value.length > MAX_LENGTH_SHORT_ID) {
                return Promise.reject(
                  `${nameValue} không quá ${MAX_LENGTH_SHORT_ID} ký tự!`
                );
              }
            }
          }

          if (typeValue == 'COMMON') {
            if (value.length < LENGTH_COMMON_SHORT_ID) {
              return Promise.reject(
                `${nameValue} phải đạt ${LENGTH_COMMON_SHORT_ID} ký tự!`
              );
            }

            if (value.length > LENGTH_COMMON_SHORT_ID) {
              return Promise.reject(
                `${nameValue} không quá ${LENGTH_COMMON_SHORT_ID} ký tự!`
              );
            }
          }

          const found = value.match(SHORT_ID_REGEX);
          if (!found) {
            return Promise.reject(`${nameValue} phải viết in hoa!`);
          }
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateLocationFieldForm = (error: any, isVerifyAll = false) => [
  {
    required: true,
    message: `Bạn chưa nhập thông tin tỉnh, huyện, xã!`,
  },
  ({ getFieldValue }: any) => {
    return {
      validator(_, value) {
        if (error) {
          return Promise.reject(
            `Bạn chưa nhập đầy đủ thông tin tỉnh, huyện, xã!`
          );
        }
        if (
          isVerifyAll &&
          value &&
          (isEmptyString(value.districtCode) ||
            isEmptyString(value.wardCode) ||
            isEmptyString(value.provinceCode))
        ) {
          return Promise.reject(
            `Bạn chưa nhập đầy đủ thông tin tỉnh, huyện, xã!`
          );
        }

        return Promise.resolve();
      },
    };
  },
];

const capitalizeFirstLetter = string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const validateEmptyStringFieldForm = (
  name: string,
  option?: {
    regex?: string;
    maxLength?: number;
    minLength?: number;
    errorRegexMessage?: string;
  }
) => [
  {
    required: true,
    message: `Bạn chưa nhập ${name}!`,
  },
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(
            `${capitalizeFirstLetter(name)} không dùng chuỗi rỗng!`
          );
        }

        if (value && value.length > 0 && checkFirstCharIsNotSpace(value)) {
          return Promise.reject(
            `${capitalizeFirstLetter(name)} không được bắt đầu là khoảng trắng!`
          );
        }

        if (value && option?.regex) {
          const regex = new RegExp(option?.regex);
          const isValid = regex.test(value);
          if (!isValid) {
            const errorMessage =
              option?.errorRegexMessage ||
              `${capitalizeFirstLetter(name)} không hợp lệ`;
            return Promise.reject(errorMessage);
          }
        }

        if (value && option?.maxLength && value.length > option?.maxLength) {
          return Promise.reject(
            `${capitalizeFirstLetter(name)} có độ dài tối đa ${
              option?.maxLength
            } kí tự`
          );
        }

        if (value && option?.minLength && value.length < option?.minLength) {
          return Promise.reject(
            `${capitalizeFirstLetter(name)} có độ dài tối thiểu ${
              option?.minLength
            } kí tự`
          );
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateURLFieldForm = ({
  name,
  type,
  isRequired = false,
}: {
  name?: string;
  type: 'HAS_PREFIX' | 'NO_HAS_PREFIX';
  isRequired?: boolean;
}) => [
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        if (isRequired && value) {
          return Promise.reject(`Bạn chưa nhập tên ${name || 'Đường dẫn'}!`);
        }

        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(
            `${name || 'Đường dẫn'} không dùng chuỗi rỗng!`
          );
        }
        if (value && value.length > 0 && checkFirstCharIsNotSpace(value)) {
          return Promise.reject(
            `${name || 'Đường dẫn'} không được bắt đầu là khoảng trắng!`
          );
        }

        const urlValue = type == 'HAS_PREFIX' ? `https://${value}` : value;

        if (value && !validateURL(urlValue) && urlValue) {
          return Promise.reject(`${name || 'Đường dẫn'} không hợp lệ!`);
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateEmailFieldForm = ({
  name = 'Email',
  isRequired,
}: {
  name?: string;
  isRequired?: boolean;
}) => [
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        if (isRequired && !value) {
          return Promise.reject(`Bạn chưa nhập tên ${name || 'Đường dẫn'}!`);
        }

        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(`${name || 'Email'} không dùng chuỗi rỗng!`);
        }

        if (value && value.length > 0 && checkFirstCharIsNotSpace(value)) {
          return Promise.reject(
            `${name || 'Email'} không được bắt đầu là khoảng trắng!`
          );
        }

        if (value && !validateEmail(value)) {
          return Promise.reject(`${name || 'Email'} không hợp lệ!`);
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateCodeForm = (
  validateDuplicateCodeAction?: any,
  defaultValue?: string
) => [
  {
    required: true,
    message: `Bạn chưa nhập mã SKU!`,
  },
  ({ getFieldValue }) => {
    return {
      async validator(_, value) {
        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(`Mã SKU không dùng chuỗi rỗng!`);
        }

        if (value) {
          const maxLength = MAX_LENGTH_SKU_CHAR + MAX_LENGTH_SKU_NUMBER;

          const patternSKU = new RegExp(
            `^([a-zA-Z]{1,${MAX_LENGTH_SKU_CHAR}})([0-9]{1,${MAX_LENGTH_SKU_NUMBER}})(?:[a-zA-Z]+)?$`
          );

          const onlyNumberPatternSKU = new RegExp(
            `^(?:[0-9]{0,${MAX_LENGTH_SKU_NUMBER}})$`
          );

          const onlyNumberPattern = /^[0-9]+$/;

          const isNumberSKU = onlyNumberPattern.test(value);
          const isValid =
            patternSKU.test(value) || onlyNumberPatternSKU.test(value);

          if (value.length > MAX_LENGTH_SKU_NUMBER && isNumberSKU) {
            return Promise.reject(
              `Mã SKU có độ dài tối đa ${MAX_LENGTH_SKU_NUMBER} kí tự số`
            );
          }

          if (value.length > maxLength) {
            return Promise.reject(`Mã SKU có độ dài tối đa ${maxLength} kí tự`);
          }
          if (!isValid) {
            return Promise.reject(
              `Mã SKU gồm 2 phần hoặc 1 phần số VD: SKU000001/000001`
            );
          }
          if (
            isValid &&
            validateDuplicateCodeAction &&
            (!defaultValue || defaultValue != value)
          ) {
            try {
              const isExistCode = await validateDuplicateCodeAction(value);
              if (isExistCode) {
                return Promise.reject('Mã SKU đã tồn tại');
              }
            } catch (error) {
              return Promise.reject('Mã SKU không hợp lệ!');
            }
          }
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateReceiptCodeForm = (
  validateDuplicateCodeAction?: any,
  defaultValue?: string
) => [
  {
    required: true,
    message: `Bạn chưa nhập số chứng từ!`,
  },
  ({ getFieldValue }) => {
    return {
      async validator(_, value) {
        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(`Số chứng từ không dùng chuỗi rỗng!`);
        }

        if (value) {
          if (value.length > 20) {
            return Promise.reject(`Số chứng từ có độ dài tối đa 20 kí tự`);
          }
          if (
            validateDuplicateCodeAction &&
            (!defaultValue || defaultValue != value)
          ) {
            try {
              const isExistCode = await validateDuplicateCodeAction(value);
              if (isExistCode) {
                return Promise.reject('Số chứng từ đã tồn tại');
              }
            } catch (error) {
              return Promise.reject('Số chứng từ không hợp lệ!');
            }
          }
        }

        return Promise.resolve();
      },
    };
  },
];

export const validateCouponCodeForm = (
  validateDuplicateCodeAction?: any,
  defaultValue?: string
) => [
  ({ getFieldValue }) => {
    return {
      async validator(_, value) {
        if (!value || value?.length == 0) {
          return Promise.reject(`Bạn chưa nhập mã giảm giá!`);
        }

        if (value && value.length > 0 && checkOnlySpaces(value)) {
          return Promise.reject(`Mã giảm giá không dùng chuỗi rỗng!`);
        }

        const isValid = COUPON_REGEX.test(value);

        if (value.length < 5) {
          return Promise.reject(`Mã giảm giá có độ dài tối thiểu 5 kí tự`);
        }

        if (value.length > 20) {
          return Promise.reject(`Mã giảm giá có độ dài tối đa 20 kí tự`);
        }

        if (!isValid) {
          return Promise.reject(
            `Mã giảm giá gồm kí tự chữ và số không bao gồm kí tự đặc biệt`
          );
        }

        if (value) {
          if (
            validateDuplicateCodeAction &&
            (!defaultValue || defaultValue != value)
          ) {
            try {
              const isExistCode = await validateDuplicateCodeAction(value);
              if (isExistCode) {
                return Promise.reject('Mã coupon đã tồn tại');
              }
            } catch (error) {
              return Promise.reject('Mã coupon không hợp lệ!');
            }
          }
        }
        return Promise.resolve();
      },
    };
  },
];

export const validateNumberFieldForm = (max?: number, min?: number) => [
  {
    required: true,
    message: `Giá trị trống!`,
  },
  ({ getFieldValue }) => {
    return {
      validator(_, value) {
        if (value && !checkOnlyNumber(value)) {
          return Promise.reject(`Giá trị là chữ số!`);
        }
        if (value && min != undefined && value < min) {
          return Promise.reject(`Giá trị > ${min}!`);
        }
        if (value && max != undefined && value > max) {
          return Promise.reject(`Giá trị < ${max}!`);
        }

        return Promise.resolve();
      },
    };
  },
];
